import { useRef, useState, useCallback, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./Table.module.scss";
import { useScrollHint } from "@/shared/hooks/useScrollHint";
import { useStickyHeader } from "./useStickyHeader";
import { TableHeader, type TableColumn } from "./TableHeader";

interface TableProps {
  children: ReactNode;
  columns: readonly TableColumn[];
  emptyMessage: string;
  onInfoClick?: (key: string) => void;
}

export function Table({
  children,
  columns,
  emptyMessage,
  onInfoClick,
}: TableProps) {
  const [showInfo, setShowInfo] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const wrapperRef = useScrollHint<HTMLDivElement>({
    delay: 500,
    distance: 40,
    duration: 600,
  });

  const measureAndApply = useCallback(() => {
    const headerEl = headerRef.current;
    const scrollEl = scrollRef.current;
    const cloneRoot = cloneRootRef.current;
    const cloneInner = cloneInnerRef.current;

    if (!headerEl || !scrollEl || !cloneRoot || !cloneInner) return;

    const cloneCells =
      cloneCellsRef.current ??
      (Array.from(cloneInner.children) as HTMLElement[]);
    cloneCellsRef.current = cloneCells;

    const scrollRect = scrollEl.getBoundingClientRect();
    const headerRect = headerEl.getBoundingClientRect();
    const headerCells = Array.from(
      headerEl.children[0].children,
    ) as HTMLElement[];

    headerCells.forEach((cell, idx) => {
      if (cloneCells[idx]) {
        cloneCells[idx].style.width =
          `${Math.round(cell.getBoundingClientRect().width)}px`;
      }
    });

    cloneRoot.style.left = `${Math.round(scrollRect.left)}px`;
    cloneRoot.style.width = `${Math.round(scrollRect.width)}px`;
    cloneRoot.style.height = `${Math.round(headerRect.height)}px`;
    cloneRoot.style.top = "0px";

    cloneInner.style.transform = `translate3d(${-scrollEl.scrollLeft}px, 0, 0)`;
  }, []);

  const {
    scrollRef,
    headerRef,
    sentinelRef,
    cloneRootRef,
    cloneInnerRef,
    cloneCellsRef,
  } = useStickyHeader(measureAndApply);

  const handleShowInfo = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    setShowInfo(true);
    setFadeOut(false);

    timeoutRef.current = setTimeout(() => {
      setFadeOut(true);
      setTimeout(() => setShowInfo(false), 300);
    }, 3000);
  }, []);

  const renderCloneHeader = () => (
    <div
      ref={cloneRootRef}
      className={styles.stickyClone}
      style={{
        display: "none",
        position: "fixed",
        top: 0,
        left: 0,
        width: "0px",
        zIndex: 9998,
      }}
      aria-hidden="true"
    >
      <div
        ref={cloneInnerRef}
        className={styles.stickyCloneInner}
        style={{ transform: "translate3d(0,0,0)" }}
      >
        {columns.map(({ key, label, suffix }) => (
          <div
            key={key}
            className={`${styles.headerCell} ${styles.stickyCell}`}
          >
            {label}
            {suffix && (
              <span
                onClick={() => onInfoClick?.(key)}
                className={styles.infoSuffix}
              >
                {suffix}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const hasChildren = Array.isArray(children)
    ? children.some((c) => c)
    : !!children;

  return (
    <div className={styles.productsTableWrapper} ref={wrapperRef}>
      <div ref={sentinelRef} style={{ height: 1, width: "100%" }} />
      <div className={styles.productsTableScroll} ref={scrollRef}>
        <div className={styles.productsTable}>
          <div ref={headerRef}>
            <TableHeader columns={columns} onInfoClick={handleShowInfo} />
          </div>
          <div className={styles.tableBody}>
            {hasChildren ? (
              children
            ) : (
              <div className={styles.emptyProducts}>{emptyMessage}</div>
            )}
          </div>
        </div>
      </div>
      {createPortal(renderCloneHeader(), document.body)}
      {showInfo &&
        createPortal(
          <div
            className={`${styles.infoToast} ${fadeOut ? styles.fadeOut : ""}`}
            onClick={() => {
              setFadeOut(true);
              setTimeout(() => setShowInfo(false), 300);
            }}
          >
            <p>
              <strong>СВР</strong> — Средний Видимый Рейтинг последних 10
              отзывов.
              <br />
              Учитывает только те отзывы, которые видны покупателю.
            </p>
          </div>,
          document.body,
        )}
    </div>
  );
}
